import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { getCurrentUser, signOut as amplifySignOut, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { authApi } from '../lib/apiClient';
import type { WllUser } from '../types';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  cognitoUser: { username: string; userId: string } | null;
  wllUser: WllUser | null;
  isAdmin: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [cognitoUser, setCognitoUser] = useState<{ username: string; userId: string } | null>(null);
  const [wllUser, setWllUser] = useState<WllUser | null>(null);

  const loadUser = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      setCognitoUser({ username: user.username, userId: user.userId });
      setIsAuthenticated(true);

      // Provision / fetch WLL user from backend
      const profile = await authApi.getMe();
      setWllUser(profile);
    } catch {
      setCognitoUser(null);
      setWllUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Listen for Amplify auth events (sign-in, sign-out, token refresh)
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          loadUser();
          break;
        case 'signedOut':
          setCognitoUser(null);
          setWllUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          break;
        case 'tokenRefresh_failure':
          console.warn('[WLL] Token refresh failed — signing out');
          signOut();
          break;
      }
    });
    return unsubscribe;
  }, [loadUser]);

  const signOut = useCallback(async () => {
    try {
      await amplifySignOut({ global: true });
    } catch (err) {
      console.error('Sign out error:', err);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  // Derive admin status from Cognito groups via session
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) { setIsAdmin(false); return; }
    fetchAuthSession()
      .then((session) => {
        const groups =
          (session.tokens?.idToken?.payload?.['cognito:groups'] as string[]) ?? [];
        setIsAdmin(groups.includes('wll-ai-admins'));
      })
      .catch(() => setIsAdmin(false));
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, cognitoUser, wllUser, isAdmin, signOut, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
